import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { 
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Bell, Bitcoin, Clock, MoreVertical, Loader2, Trash2, Eye, EyeOff, CheckCheck, Copy, GripVertical, Power, PowerOff } from "lucide-react";
import { WalletAlertForm } from "@/components/wallet-alert-form";
import { fetchWalletAlerts, fetchWalletAlertHistory, markAlertHistoryAsSeen, markAllAlertHistoryAsSeen, fetchUnseenAlertCount, reorderWalletAlerts, enableWalletAlert, disableWalletAlert } from '@/data-access/alerts';
import { deleteWalletAlert } from '@/lib/wallet-alert-api';
import { formatBitcoin } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Define the Alert type
interface Alert {
  id: number;
  createdAt: Date;
  userId: number;
  address: string;
  cryptocurrency: "bitcoin";
  alertType: "received" | "sent" | "both";
  notificationMethod: "email" | "sms" | "both";
  contactInfo: string;
  webhookId: string | null;
  active: boolean;
  lastTriggered: Date | null;
  descriptor?: {
    UserID: string;
    Descriptor: string;
    LastIndex: number[];
    Addresses?: {
      UserID: string;
      Descriptor: string;
      Cardinality: number[];
      Address: string;
      ScriptHash: string;
    }[];
  };
}

// Define the AlertHistory type
type AlertHistory = Awaited<ReturnType<typeof fetchWalletAlertHistory>>['data'][number];

// Get crypto icon component based on cryptocurrency
const CryptoIcon = ({ cryptocurrency }: { cryptocurrency: string }) => {
  if (cryptocurrency === 'bitcoin') {
    return (
      <div className="h-8 w-8 flex items-center justify-center rounded-full overflow-hidden">
        <img 
          src="/assets/btc_logo.png" 
          alt="Bitcoin" 
          className="h-8 w-8 object-contain"
        />
      </div>
    );
  }
  return (
    <div className="h-8 w-8 bg-gray-100 text-gray-600 flex items-center justify-center font-bold rounded-full">
      {cryptocurrency.charAt(0).toUpperCase()}
    </div>
  );
};

// Alert status badge styling based on active status and last triggered
const getStatusBadgeClass = (alert: Alert): string => {
  if (!alert.active) {
    return 'bg-gray-100 text-gray-800';
  }
  if (alert.lastTriggered) {
    return 'bg-yellow-100 text-yellow-800';
  }
  return 'bg-green-100 text-green-800';
};

// Get status text based on alert properties
const getStatusText = (alert: Alert): string => {
  if (!alert.active) return 'Disabled';
  if (alert.lastTriggered) return 'Triggered';
  return 'Active';
};

// Get crypto display name
const getCryptoDisplayName = (cryptocurrency: string): string => {
  switch (cryptocurrency) {
    case 'bitcoin':
      return 'Bitcoin';
    default:
      return cryptocurrency.charAt(0).toUpperCase() + cryptocurrency.slice(1);
  }
};

// Get crypto symbol
const getCryptoSymbol = (cryptocurrency: string): string => {
  switch (cryptocurrency) {
    case 'bitcoin':
      return 'BTC';
    default:
      return cryptocurrency.toUpperCase();
  }
};

// Get condition text based on alert type
const getConditionText = (alertType: string): string => {
  switch (alertType) {
    case 'received':
      return 'Incoming transactions only';
    case 'sent':
      return 'Outgoing transactions only';
    case 'both':
      return 'Any change in value';
    default:
      return 'Unknown condition';
  }
};

// Calculate days/weeks ago
const getTimeAgoText = (createdAt: Date): string => {
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - new Date(createdAt).getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks === 1) return '1 week ago';
  return `${diffWeeks} weeks ago`;
};

// Get notification method display text
const getNotificationDisplayText = (method: string): string => {
  switch (method) {
    case 'email':
      return 'Email';
    case 'sms':
      return 'SMS';
    case 'both':
      return 'Both Email & SMS';
    default:
      return method;
  }
};

// Sortable Alert Item Component
function SortableAlertItem({ alert, onDelete, onToggleActive }: {
  alert: Alert,
  onDelete: (alert: Alert) => void,
  onToggleActive: (alert: Alert, active: boolean) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: alert.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-2 px-2 rounded-md border border-solid border-gray-200 dark:border-gray-700 py-4 ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex justify-between">
        <div className="flex gap-3 flex-1">
          {/* Drag Handle */}
          <div
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded mt-1 flex-shrink-0"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>

          <CryptoIcon cryptocurrency={alert.cryptocurrency} />
          
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <span className="font-medium text-gray-900 dark:text-white">{getCryptoDisplayName(alert.cryptocurrency)}</span>
              <span className="px-2 text-black dark:text-black bg-white  dark:bg-white border py-0.5 rounded text-xs">{getCryptoSymbol(alert.cryptocurrency)}</span>
              <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadgeClass(alert)}`}>
                {getStatusText(alert)}
              </span>
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300 mb-3 font-mono">{alert.address}</div>
            {alert?.descriptor?.Addresses?.length && (
              <div className="text-sm text-gray-700 dark:text-gray-300 mb-3 font-mono">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Watching Addresses:
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {alert.descriptor.Addresses.map(a => (
                    <div key={a.Address} className="flex items-center gap-1">
                      <span>{a.Address} {a.Cardinality.length > 1 ? `(/${a.Cardinality.join('/')})` : ''}</span>
                    </div>
                  ))}
                </span>
              </div>
            )}
            <div className="grid grid-cols-2">
              <div>
                <div className="flex gap-1">
                  <div className="font-bold text-gray-500 dark:text-gray-400">Alert Condition:</div>
                  <div className="text-gray-900 dark:text-white">{getConditionText(alert.alertType)}</div>
                </div>
                
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Created {getTimeAgoText(alert.createdAt)}
                </div>
              </div>
              
              <div className="flex gap-1">
                <div className="font-bold text-gray-500 dark:text-gray-400">Notification Method:</div>
                <div className="text-gray-900 dark:text-white">{getNotificationDisplayText(alert.notificationMethod)}</div>
              </div>
            </div>
          </div>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
              <MoreVertical className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {alert.active ? (
              <DropdownMenuItem 
                onClick={() => onToggleActive(alert, false)}
                className="text-orange-600 dark:text-orange-400 focus:text-orange-600 dark:focus:text-orange-400 focus:bg-orange-50 dark:focus:bg-orange-950/30"
              >
                <PowerOff className="h-4 w-4 mr-2" />
                Disable Alert
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem 
                onClick={() => onToggleActive(alert, true)}
                className="text-green-600 dark:text-green-400 focus:text-green-600 dark:focus:text-green-400 focus:bg-green-50 dark:focus:bg-green-950/30"
              >
                <Power className="h-4 w-4 mr-2" />
                Enable Alert
              </DropdownMenuItem>
            )}
            <DropdownMenuItem 
              onClick={() => onDelete(alert)}
              className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-950/30"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Alert
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// Active Alerts Section Component
function ActiveAlertsSection({ 
  alerts, 
  hasMore, 
  onLoadMore, 
  isLoading,
  onDeleteAlert,
  onReorderAlerts,
  onToggleActive,
}: {
  alerts: Alert[];
  hasMore: boolean;
  onLoadMore: () => void;
  isLoading: boolean;
  onDeleteAlert: (alert: Alert) => void;
  onReorderAlerts: (event: DragEndEvent, alerts: Alert[]) => void;
  onToggleActive: (alert: Alert, active: boolean) => void;
}) {
  // Drag sensors for better mobile support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    onReorderAlerts(event, alerts);
  };

  if (isLoading && alerts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center px-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500 dark:text-blue-400 mb-4" />
        <p className="text-gray-600 dark:text-gray-300">Loading alerts...</p>
      </div>
    );
  }

  return (
    <div>
      {alerts.length > 0 ? (
        <div className="px-4">
          {/* Drag and Drop Alerts */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={alerts.map(a => a.id)}
              strategy={verticalListSortingStrategy}
            >
              {alerts.map((alert) => (
                <SortableAlertItem
                  key={alert.id}
                  alert={alert}
                  onDelete={onDeleteAlert}
                  onToggleActive={onToggleActive}
                />
              ))}
            </SortableContext>
          </DndContext>

          {hasMore && (
            <div className="flex justify-center">
              <button 
                onClick={onLoadMore}
                disabled={isLoading}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs mt-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Show More'
                )}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center px-4">
          <div className="h-20 w-20 bg-gray-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-6 shadow-md">
            <Bell className="h-10 w-10 text-gray-400 dark:text-blue-300" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Wallet Alerts</h3>
          <p className="text-gray-600 dark:text-gray-300 max-w-md">
            Create alerts to be notified when activity occurs on Bitcoin addresses you want to monitor.
          </p>
        </div>
      )}
    </div>
  );
}

// Alert History Section Component
function AlertHistorySection({ 
  alertHistory,
  hasMore,
  onLoadMore,
  isLoading,
  onMarkAsSeen,
  onMarkAllAsSeen,
  onToggleItemSeen,
  unseenCount
}: { 
  alertHistory: AlertHistory[];
  hasMore: boolean;
  onLoadMore: () => void;
  isLoading: boolean;
  onMarkAsSeen?: (historyIds: number[]) => void;
  onMarkAllAsSeen?: () => void;
  onToggleItemSeen?: (historyId: number, seen: boolean) => void;
  unseenCount: number;
}) {
  // Helper function to truncate transaction ID
  const truncateTxId = (txid: string | null): string => {
    if (!txid) return '';
    if (txid.length <= 30) {
      return txid;
    }
    return `${txid.slice(0, 15)}...${txid.slice(-15)}`;
  };
  // Format the status for display
  const formatStatus = (status: string): string => {
    switch (status) {
      case 'triggered':
        return 'Triggered';
      case 'sent':
        return 'Sent';
      case 'failed':
        return 'Failed';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'triggered':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'sent':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Get status badge class for history type
  const getHistoryTypeBadgeClass = (type: string | null): string => {
    switch (type) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'orphaned':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'matured':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'unconfirmed':
      default:
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
    }
  };

  // Get status badge class for history type
  const getHistoryTypeText = (type: string | null): string => {
    switch (type) {
      case 'confirmed':
        return 'Confirmed';
      case 'orphaned':
        return 'Orphaned';
      case 'matured':
        return '6 Confirmations';
      case 'unconfirmed':
      default:
        return 'Unconfirmed';
    }
  };

  const getAlertCondition = (condition: string | null) => {
    switch(condition) {
      case 'sent':
        return 'When wallet sends Bitcoin';
      case 'received':
        return 'When wallet receives Bitcoin';
      case 'both':
      default:
        return 'Both sent and received transactions'
    }
  }

  // Format amount for display
  const formatAmount = (amount: number | null, cryptocurrency: string): string => {
    if (!amount) return 'N/A';
    return `${formatBitcoin(amount / 100000000)} ${getCryptoSymbol(cryptocurrency)}`;
  };

  // Format date for relative display (like "Yesterday at 14:33")
  const formatRelativeDate = (date: Date | null): string => {
    if (!date) return 'N/A';
    
    const now = new Date();
    const alertDate = new Date(date);
    const diffTime = now.getTime() - alertDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    const timeString = alertDate.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
    
    if (diffDays === 0) {
      return `Today at ${timeString}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${timeString}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago at ${timeString}`;
    } else {
      return alertDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: alertDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      }) + ` at ${timeString}`;
    }
  };

  if (isLoading && alertHistory.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center px-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500 dark:text-blue-400 mb-4" />
        <p className="text-gray-600 dark:text-gray-300">Loading alert history...</p>
      </div>
    );
  }

  const handleMarkAllAsSeen = () => {
    if (onMarkAllAsSeen) {
      onMarkAllAsSeen();
    }
  };

  const handleItemClick = (history: AlertHistory) => {
    if (onToggleItemSeen && !history.seen) {
      onToggleItemSeen(history.id, !history.seen);
    }
  };

  const handleToggleSeen = (e: React.MouseEvent, history: AlertHistory) => {
    e.stopPropagation();
    if (onToggleItemSeen && !history.seen) {
      onToggleItemSeen(history.id, !history.seen);
    }
  };

  return (
    <div>
      {alertHistory.length > 0 ? (
        <div className="px-4">
          {/* Header with Mark All as Read button */}
          <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {unseenCount} unread total
            </span>
            {unseenCount > 0 && (
              <button
                onClick={handleMarkAllAsSeen}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-2 py-1 rounded transition-colors"
              >
                <CheckCheck className="h-3 w-3" />
                Mark All as Read
              </button>
            )}
          </div>

          {alertHistory.map((history) => (
            <div 
              key={history.id} 
              className={`mb-2 px-2 rounded-md border border-solid border-gray-200 dark:border-gray-700 py-4 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${!history.seen ? 'bg-blue-50/50 dark:bg-blue-900/10 border-l-4 border-l-blue-500 pl-3' : ''}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex gap-3 flex-1">
                  {!history.seen && (
                    <div className="h-2 w-2 bg-blue-500 rounded-full flex-shrink-0 mt-3 animate-pulse"></div>
                  )}
                  <CryptoIcon cryptocurrency={history.cryptocurrency} />
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900 dark:text-white">{getCryptoDisplayName(history.cryptocurrency)}</span>
                      <span className="px-2 text-black dark:text-black bg-white  dark:bg-white border py-0.5 rounded text-xs">
                        {getCryptoSymbol(history.cryptocurrency)}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border px-2 py-0.5 rounded text-xs">
                        {history.alertType === 'received' ? 'Incoming' : 'Outgoing'}
                      </span>
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className={`text-sm px-2 py-0.5 rounded text-xs ${getHistoryTypeBadgeClass(history.type)} border`}>
                              {getHistoryTypeText(history.type)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            {history.type === 'confirmed' && 'This transaction has reached 6 confirmations'}
                            {history.type === 'orphaned' && 'This transaction has been orphaned'}
                            {(!history.type || history.type === 'unconfirmed') && 'This transaction has not yet reached 6 confirmations'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    
                    {history.transactionId && (
                      <div className="flex items-center gap-2 mb-2">
                        <Link
                          to={`/search/transaction?query=${history.transactionId ?? ''}`} 
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm flex items-center gap-1 group font-mono"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span>{truncateTxId(history.transactionId ?? '')}</span>
                          
                        </Link>
                        <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer" onClick={(e) => {
                          e.stopPropagation();
                          if (typeof history.transactionId === 'string') {
                            navigator.clipboard.writeText(history.transactionId);
                            toast({
                              title: 'Transaction ID copied to clipboard',
                            });
                          }
                        }} />
                      </div>
                    )}
                    {history.alertCondition && (
                      <div className="mb-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          Alert Condition:&nbsp;
                        </span>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {getAlertCondition(history.alertCondition)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatAmount(history.amount, history.cryptocurrency)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {formatRelativeDate(history.triggeredAt)}
                    </div>
                  </div>
                  
                  {/* Individual mark/unmark button */}
                  
                  <button
                    onClick={(e) => handleToggleSeen(e, history)}
                    className={`h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors ${history.seen ? 'hidden' : ''}`}
                    title={history.seen ? "Read" : "Mark as read"}
                  >
                    {history.seen ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
          ))}
          {hasMore && (
            <div className="flex justify-center">
              <button 
                onClick={onLoadMore}
                disabled={isLoading}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs mt-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Show More'
                )}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center px-4">
          <div className="h-20 w-20 bg-gray-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-6 shadow-md">
            <Clock className="h-10 w-10 text-gray-400 dark:text-blue-300" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Historical Alerts</h3>
          <p className="text-gray-600 dark:text-gray-300 max-w-md">
            No historical alerts yet. Once alerts are triggered, you'll see them listed here.
          </p>
        </div>
      )}
    </div>
  );
}

export default function AlertsPage() {
  const queryClient = useQueryClient();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [alertHistory, setAlertHistory] = useState<AlertHistory[]>([]);
  const [openNewAlertModal, setOpenNewAlertModal] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState<Alert | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    hasMore: false,
    total: 0,
  });
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    hasMore: false,
    total: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [currentTab, setCurrentTab] = useState('active');

  // Listen for alert creation events to refresh the list
  useEffect(() => {
    const handleAlertCreated = () => {
      // Reset pagination and reload alerts from the beginning
      setPagination(prev => ({ ...prev, page: 1 }));
      loadAlerts(1, false);
    };

    // Listen for React Query cache invalidation events
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event?.type === 'updated' && 
          event?.query?.queryKey?.[0] === '/api/wallet-alerts' &&
          event?.query?.state?.status === 'success') {
        handleAlertCreated();
      }
    });

    return unsubscribe;
  }, []);

  // Use react-query to manage unseen count
  const { data: unseenCount = 0, refetch: refetchUnseenCount } = useQuery({
    queryKey: ['/api/wallet-alerts/history/unseen-count'],
    queryFn: async () => {
      const res = await fetchUnseenAlertCount();
      return res.count;
    },
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });

  const loadAlerts = async (page: number = 1, append: boolean = false) => {
    try {
      setIsLoading(true);
      const res = await fetchWalletAlerts(page, 10);

      if (append) {
        setAlerts(prev => [...prev, ...(res.data || [])]);
      } else {
        setAlerts(res.data || []);
      }
      
      setPagination({
        page: res.pagination.page,
        hasMore: res.pagination.hasMore,
        total: res.pagination.total,
      });
    } catch (error) {
      if (!append) {
        setAlerts([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadAlertHistory = async (page: number = 1, append: boolean = false) => {
    try {
      setIsLoadingHistory(true);
      const res = await fetchWalletAlertHistory(page, 10);

      if (append) {
        setAlertHistory(prev => [...prev, ...(res.data || [])]);
      } else {
        setAlertHistory(res.data || []);
      }
      
      setHistoryPagination({
        page: res.pagination.page,
        hasMore: res.pagination.hasMore,
        total: res.pagination.total,
      });

      // Refresh unseen count when loading new data
      if (!append) {
        refetchUnseenCount();
      }
    } catch (error) {
      if (!append) {
        setAlertHistory([]);
      }
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const markHistoryAsSeen = async (historyIds: number[]) => {
    try {
      await markAlertHistoryAsSeen(historyIds);
      
      // Update local state to mark items as seen
      setAlertHistory(prev => 
        prev.map(item => 
          historyIds.includes(item.id) 
            ? { ...item, seen: true }
            : item
        )
      );
      
      // Invalidate and refetch unseen count query
      queryClient.invalidateQueries({ queryKey: ['/api/wallet-alerts/history/unseen-count'] });
    } catch (error) {
      console.error('Failed to mark history as seen:', error);
    }
  };

  const markAllHistoryAsSeen = async () => {
    try {
      await markAllAlertHistoryAsSeen();
      
      // Update local state to mark all items as seen
      setAlertHistory(prev => 
        prev.map(item => ({ ...item, seen: true }))
      );
      
      // Invalidate and refetch unseen count query
      queryClient.invalidateQueries({ queryKey: ['/api/wallet-alerts/history/unseen-count'] });
    } catch (error) {
      console.error('Failed to mark all history as seen:', error);
    }
  };

  const handleToggleItemSeen = async (historyId: number, seen: boolean) => {
    // Update local state immediately for optimistic update
    setAlertHistory(prev => 
      prev.map(item => 
        item.id === historyId 
          ? { ...item, seen }
          : item
      )
    );

    try {
      if (seen) {
        // Mark as seen
        await markAlertHistoryAsSeen([historyId]);
      } else {
        // For marking as unseen, we would need a new API endpoint
        // For now, we'll just update local state
        console.log('Mark as unseen functionality would require new API endpoint');
      }
      
      // Invalidate and refetch unseen count query
      queryClient.invalidateQueries({ queryKey: ['/api/wallet-alerts/history/unseen-count'] });
    } catch (error) {
      console.error('Failed to toggle item seen state:', error);
      // Revert optimistic update on error
      setAlertHistory(prev => 
        prev.map(item => 
          item.id === historyId 
            ? { ...item, seen: !seen }
            : item
        )
      );
    }
  };

  const handleLoadMore = () => {
    if (!isLoading && pagination.hasMore) {
      loadAlerts(pagination.page + 1, true);
    }
  };

  const handleLoadMoreHistory = async () => {
    if (!isLoadingHistory && historyPagination.hasMore) {
      loadAlertHistory(historyPagination.page + 1, true);
    }
  };

  const handleDeleteAlert = (alert: Alert) => {
    setAlertToDelete(alert);
  };

  const handleConfirmDelete = async () => {
    if (!alertToDelete) return;
    
    try {
      setIsDeleting(true);
      await deleteWalletAlert(alertToDelete.id);
      
      // Remove the deleted alert from the current list
      setAlerts(prev => prev.filter(alert => alert.id !== alertToDelete.id));
      
      // Update pagination total
      setPagination(prev => ({
        ...prev,
        total: prev.total - 1,
      }));
      
      setAlertToDelete(null);
    } catch (error) {
      console.error('Failed to delete alert:', error);
      // You might want to show a toast notification here
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReorderAlerts = async (event: DragEndEvent, currentAlerts: Alert[]) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = currentAlerts.findIndex(alert => alert.id === active.id);
    const newIndex = currentAlerts.findIndex(alert => alert.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedAlerts = arrayMove(currentAlerts, oldIndex, newIndex);
    
    // Create the reorder payload with new order values
    const alertOrders = reorderedAlerts.map((alert, index) => ({
      id: alert.id,
      order: index
    }));

    // Update local state optimistically
    setAlerts(reorderedAlerts);

    try {
      // Send the reorder request
      await reorderWalletAlerts(alertOrders);
    } catch (error) {
      console.error('Failed to reorder alerts:', error);
      // Revert optimistic update on error
      setAlerts(currentAlerts);
    }
  };

  const handleToggleActive = async (alert: Alert, active: boolean) => {
    if (isTogglingActive) return;
    
    try {
      setIsTogglingActive(true);
      
      // Optimistically update local state
      setAlerts(prev => 
        prev.map(a => 
          a.id === alert.id 
            ? { ...a, active }
            : a
        )
      );

      // Call API to update the alert
      if (active) {
        await enableWalletAlert(alert.id);
        toast({
          title: "Alert enabled",
          description: "The wallet alert has been enabled successfully.",
        });
      } else {
        await disableWalletAlert(alert.id);
        toast({
          title: "Alert disabled",
          description: "The wallet alert has been disabled successfully.",
        });
      }
    } catch (error) {
      console.error('Failed to toggle alert active state:', error);
      // Revert optimistic update on error
      setAlerts(prev => 
        prev.map(a => 
          a.id === alert.id 
            ? { ...a, active: !active }
            : a
        )
      );
      
      toast({
        title: "Error",
        description: `Failed to ${active ? 'enable' : 'disable'} the alert.`,
        variant: "destructive",
      });
    } finally {
      setIsTogglingActive(false);
    }
  };

  const handleTabChange = (tabValue: string) => {
    setCurrentTab(tabValue);
  };

  useEffect(() => {
    loadAlerts();
    loadAlertHistory();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="max-w-screen-lg mx-auto w-full">
        <div className="flex-grow pt-2 pb-2 px-0 sm:px-2 lg:px-4">
          <div className="flex justify-between items-center mb-6 px-4 pt-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center shadow-sm">
                <Bell className="h-5 w-5" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Wallet Alerts</h1>
            </div>
            <Button
              onClick={() => setOpenNewAlertModal(true)}
              variant="default"
              size="sm"
            >
              + New Alert
            </Button>
          </div>
          
          <Tabs value={currentTab} onValueChange={handleTabChange} className="mb-6 px-4">
            <TabsList className="grid w-full grid-cols-2 gap-2">
              <TabsTrigger 
                value="active"
              >
                <Bell className="h-3.5 w-3.5 mr-2" />
                Active Alerts
              </TabsTrigger>
              <TabsTrigger 
                value="history" 
                className="relative"
              >
                <Clock className="h-3.5 w-3.5 mr-2" />
                Alert History
                {unseenCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {unseenCount > 99 ? '99+' : unseenCount}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="active">
              <ActiveAlertsSection 
                alerts={alerts}
                hasMore={pagination.hasMore}
                onLoadMore={handleLoadMore}
                isLoading={isLoading}
                onDeleteAlert={handleDeleteAlert}
                onReorderAlerts={handleReorderAlerts}
                onToggleActive={handleToggleActive}
              />
            </TabsContent>
            
            <TabsContent value="history">
              <AlertHistorySection 
                alertHistory={alertHistory}
                hasMore={historyPagination.hasMore}
                onLoadMore={handleLoadMoreHistory}
                isLoading={isLoadingHistory}
                onMarkAsSeen={markHistoryAsSeen}
                onMarkAllAsSeen={markAllHistoryAsSeen}
                onToggleItemSeen={handleToggleItemSeen}
                unseenCount={unseenCount}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      <WalletAlertForm open={openNewAlertModal} onOpenChange={setOpenNewAlertModal} />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!alertToDelete} onOpenChange={(open) => !open && setAlertToDelete(null)}>
        <AlertDialogContent className="dark:bg-gray-800 dark:border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">Delete Wallet Alert</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-300">
              {alertToDelete && (
                <div className="space-y-2 mt-2">
                  <p>You are about to delete the following alert:</p>
                  <div className="bg-muted dark:bg-gray-700 p-3 rounded-md text-sm dark:text-gray-200">
                    <p><strong>Wallet Address:</strong> {alertToDelete.address}</p>
                    <p><strong>Cryptocurrency:</strong> {getCryptoDisplayName(alertToDelete.cryptocurrency)}</p>
                    <p><strong>Alert Type:</strong> {getConditionText(alertToDelete.alertType)}</p>
                    <p><strong>Notification Method:</strong> {getNotificationDisplayText(alertToDelete.notificationMethod)}</p>
                  </div>
                  <p>This action cannot be undone. Do you want to proceed?</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              disabled={isDeleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="dark:bg-red-600 dark:hover:bg-red-700 dark:text-white"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 
